import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useLocale } from "../i18n/LocaleContext";
import VideoCard from "../components/cinema/VideoCard";
import VideoModal from "../components/cinema/VideoModal";
import CinemaCarousel from "../components/CinemaCarousel";

/**
 * Public Cinema page.
 *
 * Layout:
 *   1. Hero header (overline + title + body)
 *   2. Category tabs — Tutti · Auto · Moto (client-side filter)
 *   3. Horizontal reel (top 6 videos of the current filter)
 *   4. Full grid with every remaining video of the current filter
 *   5. Members-only "Cinema Esclusivo" rail (only when the visitor
 *      is signed in as a member)
 *
 * Video categorisation uses `vehicle_type`: "auto" / "moto" / "" (generic
 * — always visible). Filter tabs never hide "generic" clips.
 */
const TABS = [
  { key: "all", label: "Tutti" },
  { key: "auto", label: "Auto" },
  { key: "moto", label: "Moto" },
];

export default function Cinema() {
  const { t } = useLocale();
  const { isMember } = useAuth();
  const [videos, setVideos] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [active, setActive] = useState(null);
  const [tab, setTab] = useState("all");

  useEffect(() => {
    let cancelled = false;
    api
      .get("/videos")
      .then((r) => {
        if (cancelled) return;
        setVideos(Array.isArray(r.data) ? r.data : []);
      })
      .catch(() => {
        if (!cancelled) setVideos([]);
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Only public videos in the tabs/grid; members still get their exclusive
  // rail rendered separately below via <CinemaCarousel filter="exclusive"/>.
  const publicVideos = useMemo(
    () => videos.filter((v) => !v.is_member_only),
    [videos],
  );

  const filtered = useMemo(() => {
    if (tab === "all") return publicVideos;
    return publicVideos.filter((v) => {
      const vt = (v.vehicle_type || "").toLowerCase();
      // Generic clips (empty vehicle_type) are always visible on every tab.
      if (!vt) return true;
      return vt === tab;
    });
  }, [publicVideos, tab]);

  return (
    <div className="min-h-screen pt-28 sm:pt-32 gd-grain" data-testid="cinema-page">
      <section className="px-6 sm:px-12 mb-10 sm:mb-14">
        <div className="max-w-7xl mx-auto">
          <motion.span
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="overline mb-4 block"
          >
            {t("cinema.overline")}
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-3xl sm:text-5xl lg:text-6xl font-light text-balance"
            style={{ letterSpacing: "-0.01em" }}
            data-testid="cinema-headline"
          >
            {t("cinema.headline")}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="text-sm sm:text-base max-w-2xl mt-5 sm:mt-6"
            style={{ color: "var(--text-secondary)" }}
          >
            {t("cinema.body")}
          </motion.p>

          {/* Canale YouTube + Instagram */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.45 }}
            className="mt-8 flex flex-wrap gap-3"
          >
            <a
              href="https://www.youtube.com/channel/UCs68SjdTp0KWzMEiKiHy1sw"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 text-[13px] tracking-wide"
              style={{ background: "#FF0000", color: "#FFFFFF", fontWeight: 500 }}
              data-testid="cinema-youtube-channel"
            >
              Vai al canale YouTube
            </a>
            <a
              href="https://www.instagram.com/garagedrops?stkn=Nmx4cTIyNmtna3dz&utm_source=qr"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 text-[13px] tracking-wide"
              style={{
                background: "linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)",
                color: "#FFFFFF",
                fontWeight: 500,
              }}
              data-testid="cinema-instagram"
            >
              Seguici su Instagram
            </a>
          </motion.div>
        </div>
      </section>

      {/* Category tabs */}
      <section className="px-6 sm:px-12 mb-8 sm:mb-10">
        <div
          className="max-w-7xl mx-auto flex items-center gap-2 sm:gap-3 flex-wrap"
          data-testid="cinema-tabs"
        >
          {TABS.map((t2) => {
            const activeTab = tab === t2.key;
            const count =
              t2.key === "all"
                ? publicVideos.length
                : publicVideos.filter((v) => {
                    const vt = (v.vehicle_type || "").toLowerCase();
                    if (!vt) return true;
                    return vt === t2.key;
                  }).length;
            return (
              <button
                key={t2.key}
                type="button"
                onClick={() => setTab(t2.key)}
                className="text-[11px] tracking-widest uppercase px-4 py-2 transition-colors"
                style={{
                  background: activeTab
                    ? "var(--accent-red)"
                    : "transparent",
                  color: activeTab
                    ? "#FFFFFF"
                    : "var(--text-secondary)",
                  border: `1px solid ${
                    activeTab ? "var(--accent-red)" : "var(--border-subtle)"
                  }`,
                  fontWeight: activeTab ? 500 : 400,
                }}
                data-testid={`cinema-tab-${t2.key}`}
              >
                {t2.label}
                <span
                  className="ml-2 text-[10px]"
                  style={{
                    opacity: 0.7,
                  }}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </section>

      {/* Vertical grid — every video in the current filter, no horizontal
          scroller. The first entry is rendered larger (featured) so the
          page still has a strong hero-clip without forcing sideways
          scrolling. */}
      {loaded && filtered.length > 0 && (
        <section
          className="px-6 sm:px-12 pb-16 sm:pb-24"
          data-testid="cinema-grid-section"
        >
          <div className="max-w-7xl mx-auto">
            <div
              className="grid gap-6 sm:gap-8"
              style={{
                gridTemplateColumns:
                  "repeat(auto-fill, minmax(min(100%, 320px), 1fr))",
              }}
              data-testid="cinema-grid"
            >
              {filtered.map((v, i) => (
                <VideoCard
                  key={v.id}
                  video={v}
                  onOpen={setActive}
                  index={i}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Empty state for the current tab */}
      {loaded && filtered.length === 0 && (
        <section
          className="py-24 text-center"
          data-testid="cinema-empty-state"
        >
          <p
            className="text-sm"
            style={{ color: "var(--text-secondary)" }}
          >
            Nessun episodio in questa categoria (ancora).
          </p>
        </section>
      )}

      {/* Members-only exclusive reel */}
      {isMember ? (
        <CinemaCarousel
          filter="exclusive"
          overline={t("cinema.exclusiveOverline")}
          title={t("cinema.exclusiveTitle")}
        />
      ) : (
        <section
          className="py-24 px-6 sm:px-12 border-t text-center"
          style={{ borderColor: "var(--border-subtle)" }}
          data-testid="cinema-exclusive-locked"
        >
          <span
            className="overline mb-4 block"
            style={{ color: "var(--accent-red)" }}
          >
            ● {t("cinema.exclusiveOverline")}
          </span>
          <h3 className="text-2xl sm:text-3xl font-light text-balance mb-6 max-w-xl mx-auto">
            {t("cinema.exclusiveLockedHeadline")}
          </h3>
          <p
            className="text-sm max-w-md mx-auto mb-8"
            style={{ color: "var(--text-secondary)" }}
          >
            {t("cinema.exclusiveLockedBody")}
          </p>
          <Link
            to="/register"
            className="gd-button gd-button-ferrari"
            data-testid="cinema-exclusive-cta"
          >
            {t("nav.enterVault")}
          </Link>
        </section>
      )}

      <AnimatePresence>
        {active && <VideoModal video={active} onClose={() => setActive(null)} />}
      </AnimatePresence>
    </div>
  );
}
